'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import ListingsMap from './ListingsMap';
import SavePropertyButton from './SavePropertyButton';
import { getListingHref, isSoldStatus, type MLSProperty, type SortOption } from '@/lib/listings';
import PropertyVitals from '@/components/PropertyVitals';
interface ListingsContentProps {
  listings: MLSProperty[];
  currentPage: number;
  totalPages: number;
  total: number;
  searchParams: string;
  currentSort: SortOption;
  hasLocationFilter?: boolean;
  template?: 'classic' | 'luxury' | 'modern' | 'custom-one' | 'rcsothebys-custom';
  listingsPerRow?: 2 | 3;
  onSortChange?: (sort: SortOption) => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  googleMapsApiKey?: string;
  mlsWithVideos?: string[];
  mlsWithMatterport?: string[];
  teamAgentMlsIds?: string[];
}
function formatPrice(price: number | null): string {
  if (!price) return 'Price N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
}

// Extract just the street address, removing city/state/zip if present
function getStreetAddress(fullAddress: string | null, city: string | null, state: string | null, zipCode: string | null): string {
  if (!fullAddress) return 'Address not available';
  let streetAddress = fullAddress;

  // Remove zip code if present at the end
  if (zipCode) {
    streetAddress = streetAddress.replace(new RegExp(`\\s*,?\\s*${zipCode}\\s*$`), '');
  }

  // Remove state if present at the end (with or without comma)
  if (state) {
    streetAddress = streetAddress.replace(new RegExp(`\\s*,?\\s*${state}\\s*$`, 'i'), '');
  }

  // Remove city if present at the end (with or without comma)
  if (city) {
    streetAddress = streetAddress.replace(new RegExp(`\\s*,?\\s*${city}\\s*$`, 'i'), '');
  }
  return streetAddress.trim() || 'Address not available';
}

// Property card - style varies by template
function PropertyCard(t0) {
  const $ = _c(117);
  const {
    listing,
    template: t1,
    hasVideo: t2,
    hasMatterport: t3
  } = t0;
  const template = t1 === undefined ? "classic" : t1;
  const hasVideo = t2 === undefined ? false : t2;
  const hasMatterport = t3 === undefined ? false : t3;
  const isModernStyle = template === "modern" || template === "custom-one";
  const isRCSothebys = template === "rcsothebys-custom";
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  let T0;
  let bannerClasses;
  let bannerLabel;
  let isSold;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== currentPhotoIndex || $[1] !== imageError || $[2] !== isModernStyle || $[3] !== isRCSothebys || $[4] !== listing || $[5] !== template) {
    const photos = listing.photos && listing.photos.length > 0 ? listing.photos : [];
    const hasMultiplePhotos = photos.length > 1;
    const currentPhoto = photos[currentPhotoIndex] || null;
    let t10;
    if ($[16] !== listing.status) {
      t10 = isSoldStatus(listing.status);
      $[16] = listing.status;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    isSold = t10;
    const isRental = !!listing.is_rental;
    let t11;
    if ($[18] !== isSold || $[19] !== listing.sold_date) {
      t11 = isSold ? listing.sold_date?.match(/^(\d{4})/)?.[1] : undefined;
      $[18] = isSold;
      $[19] = listing.sold_date;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    const soldYear = t11;
    const soldLabel = isRental ? "Rented" : "Sold";
    let t12;
    if ($[21] !== isRental || $[22] !== isSold || $[23] !== soldLabel || $[24] !== soldYear) {
      t12 = isSold ? [soldLabel, soldYear].filter(Boolean).join(" ") : isRental ? "Rental" : null;
      $[21] = isRental;
      $[22] = isSold;
      $[23] = soldLabel;
      $[24] = soldYear;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    bannerLabel = t12;
    bannerClasses = isSold ? "bg-[var(--color-charcoal,#2b2b2b)]/95" : "bg-[var(--rc-navy,#002349)]/95";
    const handlePrevPhoto = e => {
      e.preventDefault();
      e.stopPropagation();
      setImageError(false);
      setCurrentPhotoIndex(prev => prev === 0 ? photos.length - 1 : prev - 1);
    };
    const handleNextPhoto = e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      setImageError(false);
      setCurrentPhotoIndex(prev_0 => prev_0 === photos.length - 1 ? 0 : prev_0 + 1);
    };
    const cardContainerClasses = template === "classic" ? "border border-gray-200 overflow-hidden" : isRCSothebys ? "border border-[var(--rc-brown)]/30 bg-[var(--rc-cream)] overflow-hidden hover:border-[var(--rc-gold)] transition-all duration-300" : isModernStyle ? "border border-[var(--modern-gray-lighter)] bg-white overflow-hidden hover:border-[var(--modern-gold)] transition-all duration-300" : "";
    const aspectRatioClasses = template === "luxury" ? "aspect-[4/5]" : isModernStyle ? "aspect-[4/3]" : "aspect-[4/3]";
    t9 = `group ${cardContainerClasses}`;
    T0 = Link;
    if ($[26] !== listing) {
      t7 = getListingHref(listing);
      $[26] = listing;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    t8 = "block";
    t4 = `relative ${aspectRatioClasses} overflow-hidden bg-[var(--color-taupe)] ${template === "luxury" ? "border border-transparent group-hover:border-[var(--color-gold)] transition-[border-color] duration-500 ease-in-out" : ""}`;
    t5 = currentPhoto && !imageError ? <Image src={currentPhoto} alt={listing.address || "Property"} fill={true} className="object-cover transition-transform duration-700 ease-out group-hover:scale-105" sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" onError={() => setImageError(true)} /> : <div className="absolute inset-0 flex items-center justify-center"><svg className="w-12 h-12 text-[var(--color-sand)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg></div>;
    t6 = hasMultiplePhotos && <><button onClick={handlePrevPhoto} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[var(--color-charcoal)] shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300" aria-label="Previous photo"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" /></svg></button><button onClick={handleNextPhoto} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[var(--color-charcoal)] shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300" aria-label="Next photo"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" /></svg></button><div className={`absolute ${bannerLabel ? "bottom-11" : "bottom-3"} left-1/2 -translate-x-1/2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>{photos.slice(0, 5).map((_, idx) => <div key={idx} className={`w-1.5 h-1.5 rounded-full transition-colors ${idx === currentPhotoIndex ? "bg-white" : "bg-white/50"}`} />)}{photos.length > 5 && <span className="text-white text-[10px] ml-1">+{photos.length - 5}</span>}</div></>;
    $[0] = currentPhotoIndex;
    $[1] = imageError;
    $[2] = isModernStyle;
    $[3] = isRCSothebys;
    $[4] = listing;
    $[5] = template;
    $[6] = T0;
    $[7] = bannerClasses;
    $[8] = bannerLabel;
    $[9] = isSold;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    T0 = $[6];
    bannerClasses = $[7];
    bannerLabel = $[8];
    isSold = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  let t10;
  if ($[28] !== bannerClasses || $[29] !== bannerLabel) {
    t10 = bannerLabel && <div className={`absolute bottom-0 inset-x-0 py-2 text-center text-[11px] uppercase tracking-[0.2em] font-medium text-white ${bannerClasses}`}>{bannerLabel}</div>;
    $[28] = bannerClasses;
    $[29] = bannerLabel;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== listing.status) {
    t11 = listing.status?.toLowerCase().startsWith("pending") && <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium bg-amber-500 text-white">Pending</span>;
    $[31] = listing.status;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== listing.listing_date) {
    t12 = listing.listing_date && (() => {
      const daysDiff = Math.floor((Date.now() - new Date(listing.listing_date).getTime()) / 86400000);
      return daysDiff <= 14;
    })() && <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium bg-[var(--rc-gold,var(--color-gold,#c19b5f))] text-white">New Listing</span>;
    $[33] = listing.listing_date;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== listing.open_house_date) {
    t13 = listing.open_house_date && new Date(listing.open_house_date + "T23:59:59") >= new Date() && <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium bg-[var(--rc-navy,#002349)] text-white">Open House {new Date(listing.open_house_date + "T00:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      })}</span>;
    $[35] = listing.open_house_date;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t11 || $[38] !== t12 || $[39] !== t13) {
    t14 = <div className="absolute top-3 left-3 flex flex-col gap-1.5">{t11}{t12}{t13}</div>;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== hasMatterport || $[42] !== hasVideo) {
    t15 = (hasVideo || hasMatterport) && <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">{hasVideo && <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium bg-[var(--rc-navy,#002349)] text-white flex items-center gap-1.5"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>Video</span>}{hasMatterport && <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-medium bg-[var(--rc-gold,#c19b5f)] text-white flex items-center gap-1.5"><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Virtual Tour</span>}</div>;
    $[41] = hasMatterport;
    $[42] = hasVideo;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const t16 = `absolute ${hasVideo || hasMatterport ? hasVideo && hasMatterport ? "top-[4.5rem]" : "top-12" : "top-3"} right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300`;
  let t17;
  if ($[44] !== listing.id) {
    t17 = <SavePropertyButton listingId={listing.id} listingType="mls" />;
    $[44] = listing.id;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t16 || $[47] !== t17) {
    t18 = <div className={t16}>{t17}</div>;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t10 || $[50] !== t14 || $[51] !== t15 || $[52] !== t18 || $[53] !== t4 || $[54] !== t5 || $[55] !== t6) {
    t19 = <div className={t4}>{t5}{t6}{t10}{t14}{t15}{t18}</div>;
    $[49] = t10;
    $[50] = t14;
    $[51] = t15;
    $[52] = t18;
    $[53] = t4;
    $[54] = t5;
    $[55] = t6;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== T0 || $[58] !== t19 || $[59] !== t7 || $[60] !== t8) {
    t20 = <T0 href={t7} className={t8}>{t19}</T0>;
    $[57] = T0;
    $[58] = t19;
    $[59] = t7;
    $[60] = t8;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  const t21 = template === "classic" ? "p-4" : isModernStyle ? "p-5" : "pl-[10px] pt-1 pb-2";
  const t22 = `line-clamp-1 ${template === "luxury" ? "text-[var(--color-charcoal)] font-luxury" : isModernStyle ? "text-[var(--modern-gold)] font-light tracking-wider" : "text-gray-900 font-semibold"}`;
  let t23;
  if ($[62] !== isModernStyle || $[63] !== template) {
    t23 = template === "luxury" ? {
      fontSize: "20px",
      fontWeight: 400,
      lineHeight: 1.3,
      letterSpacing: "0.02em",
      marginBottom: "1px"
    } : isModernStyle ? {
      fontSize: "1.25rem",
      fontWeight: 300,
      lineHeight: 1.2,
      marginBottom: "0.5rem"
    } : {
      fontSize: "1.125rem",
      lineHeight: 1.2,
      marginBottom: "0.25rem"
    };
    $[62] = isModernStyle;
    $[63] = template;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== listing.list_price) {
    t24 = formatPrice(listing.list_price);
    $[65] = listing.list_price;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== isSold || $[68] !== listing.sold_price || $[69] !== template) {
    t25 = isSold && listing.sold_price && <span className={`text-xs font-light ml-2 ${template === "luxury" ? "text-[var(--color-warm-gray)]" : "text-gray-500"}`}>Sold: {formatPrice(listing.sold_price)}</span>;
    $[67] = isSold;
    $[68] = listing.sold_price;
    $[69] = template;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== t22 || $[72] !== t23 || $[73] !== t24 || $[74] !== t25) {
    t26 = <h3 className={t22} style={t23}>{t24}{t25}</h3>;
    $[71] = t22;
    $[72] = t23;
    $[73] = t24;
    $[74] = t25;
    $[75] = t26;
  } else {
    t26 = $[75];
  }
  const t27 = `leading-snug line-clamp-1 ${template === "luxury" ? "text-[var(--color-warm-gray)] font-light font-luxury" : isModernStyle ? "text-sm text-[var(--modern-dark)] font-normal tracking-wide" : "text-sm text-gray-700"}`;
  let t28;
  if ($[76] !== template) {
    t28 = template === "luxury" ? {
      fontSize: "16px",
      marginBottom: "0"
    } : {
      marginBottom: "0.125rem"
    };
    $[76] = template;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  let t29;
  if ($[78] !== listing.address || $[79] !== listing.city || $[80] !== listing.state || $[81] !== listing.zip_code) {
    t29 = getStreetAddress(listing.address, listing.city, listing.state, listing.zip_code);
    $[78] = listing.address;
    $[79] = listing.city;
    $[80] = listing.state;
    $[81] = listing.zip_code;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  let t30;
  if ($[83] !== t27 || $[84] !== t28 || $[85] !== t29) {
    t30 = <p className={t27} style={t28}>{t29}</p>;
    $[83] = t27;
    $[84] = t28;
    $[85] = t29;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  const t31 = `leading-snug line-clamp-1 ${template === "luxury" ? "text-[var(--color-warm-gray)] font-light font-luxury" : isModernStyle ? "text-xs text-[var(--modern-gray)] uppercase tracking-[0.1em]" : "text-xs text-gray-500"}`;
  let t32;
  if ($[87] !== template) {
    t32 = template === "luxury" ? {
      fontSize: "16px",
      marginBottom: "4px"
    } : {
      marginBottom: "0.5rem"
    };
    $[87] = template;
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  let t33;
  if ($[89] !== listing.city || $[90] !== listing.state) {
    t33 = [listing.city, listing.state].filter(Boolean);
    $[89] = listing.city;
    $[90] = listing.state;
    $[91] = t33;
  } else {
    t33 = $[91];
  }
  const t34 = t33.join(", ");
  const t35 = listing.zip_code ? ` ${listing.zip_code}` : "";
  let t36;
  if ($[92] !== t31 || $[93] !== t32 || $[94] !== t34 || $[95] !== t35) {
    t36 = <p className={t31} style={t32}>{t34}{t35}</p>;
    $[92] = t31;
    $[93] = t32;
    $[94] = t34;
    $[95] = t35;
    $[96] = t36;
  } else {
    t36 = $[96];
  }
  const t37 = `text-sm ${template === "luxury" ? "text-[var(--color-warm-gray)]" : isModernStyle ? "text-[var(--modern-gray)] pt-3 border-t border-[var(--modern-gray-lighter)] mb-2" : "text-gray-600"}`;
  let t38;
  if ($[97] !== listing.bathrooms || $[98] !== listing.bedrooms || $[99] !== listing.lot_size || $[100] !== listing.square_feet || $[101] !== t37) {
    t38 = <PropertyVitals bedrooms={listing.bedrooms} bathrooms={listing.bathrooms} squareFeet={listing.square_feet} acres={listing.lot_size} className={t37} />;
    $[97] = listing.bathrooms;
    $[98] = listing.bedrooms;
    $[99] = listing.lot_size;
    $[100] = listing.square_feet;
    $[101] = t37;
    $[102] = t38;
  } else {
    t38 = $[102];
  }
  let t39;
  if ($[103] !== isModernStyle || $[104] !== listing) {
    t39 = isModernStyle && <Link href={getListingHref(listing)} className="inline-flex items-center gap-2 mt-4 text-[10px] uppercase tracking-[0.2em] text-[var(--modern-dark)] hover:text-[var(--modern-gold)] transition-colors duration-300 group/link"><span>View Details</span><svg className="w-3 h-3 transform group-hover/link:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg></Link>;
    $[103] = isModernStyle;
    $[104] = listing;
    $[105] = t39;
  } else {
    t39 = $[105];
  }
  let t40;
  if ($[106] !== t21 || $[107] !== t26 || $[108] !== t30 || $[109] !== t36 || $[110] !== t38 || $[111] !== t39) {
    t40 = <div className={t21}>{t26}{t30}{t36}{t38}{t39}</div>;
    $[106] = t21;
    $[107] = t26;
    $[108] = t30;
    $[109] = t36;
    $[110] = t38;
    $[111] = t39;
    $[112] = t40;
  } else {
    t40 = $[112];
  }
  let t41;
  if ($[113] !== t20 || $[114] !== t40 || $[115] !== t9) {
    t41 = <div className={t9}>{t20}{t40}</div>;
    $[113] = t20;
    $[114] = t40;
    $[115] = t9;
    $[116] = t41;
  } else {
    t41 = $[116];
  }
  return t41;
}
export default function ListingsContent(t0) {
  const $ = _c(72);
  const {
    listings,
    currentPage,
    totalPages,
    total,
    searchParams,
    currentSort,
    hasLocationFilter,
    template: t1,
    listingsPerRow,
    onSortChange,
    onLoadMore,
    isLoadingMore: t2,
    googleMapsApiKey,
    mlsWithVideos: t3,
    mlsWithMatterport: t4,
    teamAgentMlsIds: t5
  } = t0;
  const template = t1 === undefined ? "classic" : t1;
  const isLoadingMore = t2 === undefined ? false : t2;
  let t6;
  if ($[0] !== t3) {
    t6 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const mlsWithVideos = t6;
  let t7;
  if ($[2] !== t4) {
    t7 = t4 === undefined ? [] : t4;
    $[2] = t4;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const mlsWithMatterport = t7;
  let t8;
  if ($[4] !== t5) {
    t8 = t5 === undefined ? [] : t5;
    $[4] = t5;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const teamAgentMlsIds = t8;
  const router = useRouter();
  const [viewMode, setViewMode] = useState("list");
  const [areaFilteredListings, setAreaFilteredListings] = useState(null);
  const gridSentinelRef = useRef(null);
  const mapSentinelRef = useRef(null);
  const lastTriggeredPage = useRef(0);
  const canLoadMore = areaFilteredListings === null && currentPage < totalPages && !!onLoadMore;
  let t10;
  let t9;
  if ($[6] !== canLoadMore || $[7] !== currentPage || $[8] !== isLoadingMore || $[9] !== onLoadMore || $[10] !== viewMode) {
    t9 = () => {
      if (!canLoadMore || isLoadingMore) {
        return;
      }
      const sentinel = viewMode === "map" ? mapSentinelRef.current : gridSentinelRef.current;
      if (!sentinel) {
        return;
      }
      const root = viewMode === "map" ? typeof document !== "undefined" ? document.getElementById("listings-scroll-container") : null : null;
      const observer = new IntersectionObserver(entries => {
        if (!entries[0]?.isIntersecting) {
          return;
        }
        if (lastTriggeredPage.current === currentPage) {
          return;
        }
        lastTriggeredPage.current = currentPage;
        onLoadMore?.();
      }, {
        root,
        rootMargin: "600px"
      });
      observer.observe(sentinel);
      return () => observer.disconnect();
    };
    t10 = [canLoadMore, isLoadingMore, currentPage, viewMode, onLoadMore];
    $[6] = canLoadMore;
    $[7] = currentPage;
    $[8] = isLoadingMore;
    $[9] = onLoadMore;
    $[10] = viewMode;
    $[11] = t10;
    $[12] = t9;
  } else {
    t10 = $[11];
    t9 = $[12];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      if (window.matchMedia("(min-width: 1024px)").matches) {
        setViewMode("map");
      }
    };
    t12 = [];
    $[13] = t11;
    $[14] = t12;
  } else {
    t11 = $[13];
    t12 = $[14];
  }
  useEffect(t11, t12);
  const displayListings = areaFilteredListings !== null ? areaFilteredListings : listings;
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = filteredListings => {
      setAreaFilteredListings(filteredListings);
    };
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const handleDrawComplete = t13;
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setAreaFilteredListings(null);
    };
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const handleDrawClear = t14;
  let t15;
  if ($[17] !== onSortChange || $[18] !== router || $[19] !== searchParams) {
    t15 = newSort => {
      if (onSortChange) {
        onSortChange(newSort);
      } else {
        const params = new URLSearchParams(searchParams);
        if (newSort === "newest") {
          params.delete("sort");
        } else {
          params.set("sort", newSort);
        }
        params.delete("page");
        router.push(`/real-estate-for-sale?${params.toString()}`);
      }
    };
    $[17] = onSortChange;
    $[18] = router;
    $[19] = searchParams;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const handleSortChange = t15;
  if (listings.length === 0) {
    let t16;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <div className="flex-1 flex items-center justify-center py-16"><div className="text-center"><svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg><h3 className="mt-4 text-lg font-medium text-gray-900">No listings found</h3><p className="mt-2 text-gray-500">Try adjusting your filters to find more properties.</p></div></div>;
      $[21] = t16;
    } else {
      t16 = $[21];
    }
    return t16;
  }
  let t16;
  if ($[22] !== areaFilteredListings || $[23] !== total) {
    t16 = <span className="text-sm text-gray-600">{areaFilteredListings !== null ? <><span className="font-medium text-gray-900">{areaFilteredListings.length.toLocaleString()}</span> of {total.toLocaleString()} properties</> : <><span className="font-medium text-gray-900">{total.toLocaleString()}</span> properties found</>}</span>;
    $[22] = areaFilteredListings;
    $[23] = total;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="w-px h-4 bg-gray-200" />;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== handleSortChange) {
    t18 = e => handleSortChange(e.target.value as SortOption);
    $[26] = handleSortChange;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <option value="newest">Newest</option>;
    t20 = <option value="price_low">Price: Low to High</option>;
    t21 = <option value="price_high">Price: High to Low</option>;
    t22 = <option value="beds_low">Beds: Low to High</option>;
    t23 = <option value="beds_high">Beds: High to Low</option>;
    $[28] = t19;
    $[29] = t20;
    $[30] = t21;
    $[31] = t22;
    $[32] = t23;
  } else {
    t19 = $[28];
    t20 = $[29];
    t21 = $[30];
    t22 = $[31];
    t23 = $[32];
  }
  let t24;
  if ($[33] !== currentSort || $[34] !== t18) {
    t24 = <select value={currentSort} onChange={t18} className="h-[34px] px-3 text-sm border border-gray-200 bg-white text-gray-700 focus:border-[var(--rc-navy,#002349)] focus:ring-1 focus:ring-[var(--rc-navy,#002349)] focus:outline-none cursor-pointer">{t19}{t20}{t21}{t22}{t23}</select>;
    $[33] = currentSort;
    $[34] = t18;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] !== areaFilteredListings) {
    t25 = areaFilteredListings !== null && <button onClick={handleDrawClear} className="flex items-center gap-1 text-sm text-[var(--rc-navy,#002349)] hover:underline font-medium"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>Clear area</button>;
    $[36] = areaFilteredListings;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] !== t16 || $[39] !== t24 || $[40] !== t25) {
    t26 = <div className="flex items-center gap-3">{t16}{t17}{t24}{t25}</div>;
    $[38] = t16;
    $[39] = t24;
    $[40] = t25;
    $[41] = t26;
  } else {
    t26 = $[41];
  }
  let t27;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => setViewMode("list");
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  const t28 = `h-[34px] px-4 text-sm font-medium border flex items-center gap-2 transition-colors ${viewMode === "list" ? "bg-[var(--rc-navy,#002349)] text-white border-[var(--rc-navy,#002349)]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`;
  let t29;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>;
    $[43] = t29;
  } else {
    t29 = $[43];
  }
  let t30;
  if ($[44] !== t28) {
    t30 = <button type="button" onClick={t27} className={t28}>{t29}List</button>;
    $[44] = t28;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setViewMode("map");
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  const t32 = `h-[34px] px-4 text-sm font-medium border-t border-r border-b flex items-center gap-2 transition-colors ${viewMode === "map" ? "bg-[var(--rc-navy,#002349)] text-white border-[var(--rc-navy,#002349)]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`;
  let t33;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>;
    $[47] = t33;
  } else {
    t33 = $[47];
  }
  let t34;
  if ($[48] !== t32) {
    t34 = <button type="button" onClick={t31} className={t32}>{t33}Map</button>;
    $[48] = t32;
    $[49] = t34;
  } else {
    t34 = $[49];
  }
  let t35;
  if ($[50] !== t30 || $[51] !== t34) {
    t35 = <div className="flex" role="group">{t30}{t34}</div>;
    $[50] = t30;
    $[51] = t34;
    $[52] = t35;
  } else {
    t35 = $[52];
  }
  let t36;
  if ($[53] !== t26 || $[54] !== t35) {
    t36 = <div className="px-4 py-2.5 bg-white border-b flex items-center justify-between lg:px-6 flex-shrink-0">{t26}{t35}</div>;
    $[53] = t26;
    $[54] = t35;
    $[55] = t36;
  } else {
    t36 = $[55];
  }
  let t37;
  if ($[56] !== canLoadMore || $[57] !== displayListings || $[58] !== googleMapsApiKey || $[59] !== hasLocationFilter || $[60] !== isLoadingMore || $[61] !== listings || $[62] !== listingsPerRow || $[63] !== mlsWithMatterport || $[64] !== mlsWithVideos || $[65] !== teamAgentMlsIds || $[66] !== template || $[67] !== viewMode) {
    t37 = <div className="flex-1 flex min-h-0">{viewMode === "list" ? <div id="listings-scroll-container" className="w-full overflow-y-auto p-4 lg:p-6 bg-[var(--color-cream)]"><div className="max-w-7xl mx-auto">{displayListings.length === 0 ? <div className="text-center py-12"><svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg><h3 className="mt-4 text-lg font-medium text-gray-900">No properties in this area</h3><p className="mt-2 text-gray-500">Try drawing a larger area or clear the area filter.</p></div> : <><div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${listingsPerRow !== 2 ? "lg:grid-cols-3 xl:grid-cols-4" : ""}`}>{displayListings.map(listing => <PropertyCard key={listing.id} listing={listing} template={template} hasVideo={!isSoldStatus(listing.status) && !!listing.mls_number && mlsWithVideos.includes(listing.mls_number) && !!listing.list_agent_mls_id && teamAgentMlsIds.includes(listing.list_agent_mls_id)} hasMatterport={!isSoldStatus(listing.status) && (!!listing.mls_number && mlsWithMatterport.includes(listing.mls_number) && !!listing.list_agent_mls_id && teamAgentMlsIds.includes(listing.list_agent_mls_id) || !!listing.virtual_tour_url && !!listing.list_agent_mls_id && teamAgentMlsIds.includes(listing.list_agent_mls_id))} />)}</div>{canLoadMore && <div ref={gridSentinelRef} aria-hidden={true} className="h-1 w-full" />}{canLoadMore && isLoadingMore && <div className="flex justify-center items-center gap-2 mt-10 text-[var(--rc-navy,#002349)] text-xs uppercase tracking-[0.15em]"><svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Loading more</div>}</>}</div></div> : <><div id="listings-scroll-container" className="w-full lg:w-1/2 overflow-y-auto p-4 lg:p-6 bg-[var(--color-cream)]">{displayListings.length === 0 ? <div className="text-center py-12"><svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg><h3 className="mt-4 text-lg font-medium text-gray-900">No properties in this area</h3><p className="mt-2 text-gray-500">Try drawing a larger area or clear the area filter.</p></div> : <><div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${listingsPerRow !== 2 ? "xl:grid-cols-3" : ""}`}>{displayListings.map(listing_0 => <PropertyCard key={listing_0.id} listing={listing_0} template={template} hasVideo={!isSoldStatus(listing_0.status) && !!listing_0.mls_number && mlsWithVideos.includes(listing_0.mls_number) && !!listing_0.list_agent_mls_id && teamAgentMlsIds.includes(listing_0.list_agent_mls_id)} hasMatterport={!isSoldStatus(listing_0.status) && (!!listing_0.mls_number && mlsWithMatterport.includes(listing_0.mls_number) && !!listing_0.list_agent_mls_id && teamAgentMlsIds.includes(listing_0.list_agent_mls_id) || !!listing_0.virtual_tour_url && !!listing_0.list_agent_mls_id && teamAgentMlsIds.includes(listing_0.list_agent_mls_id))} />)}</div>{canLoadMore && <div ref={mapSentinelRef} aria-hidden={true} className="h-1 w-full" />}{canLoadMore && isLoadingMore && <div className="flex justify-center items-center gap-2 mt-10 text-[var(--rc-navy,#002349)] text-xs uppercase tracking-[0.15em]"><svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Loading more</div>}</>}</div><div className="hidden lg:block lg:w-1/2 h-full"><ListingsMap listings={listings} onDrawComplete={handleDrawComplete} onDrawClear={handleDrawClear} hasLocationFilter={hasLocationFilter} googleMapsApiKey={googleMapsApiKey} /></div></>}</div>;
    $[56] = canLoadMore;
    $[57] = displayListings;
    $[58] = googleMapsApiKey;
    $[59] = hasLocationFilter;
    $[60] = isLoadingMore;
    $[61] = listings;
    $[62] = listingsPerRow;
    $[63] = mlsWithMatterport;
    $[64] = mlsWithVideos;
    $[65] = teamAgentMlsIds;
    $[66] = template;
    $[67] = viewMode;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] !== t36 || $[70] !== t37) {
    t38 = <div className="h-full flex flex-col overflow-hidden">{t36}{t37}</div>;
    $[69] = t36;
    $[70] = t37;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  return t38;
}